/* @jsx h */
import { defineComponent, ref, onMounted, h } from "vue";
import { api } from "@app/data/index";
import { devicesAPI } from "@app/data/api/devices";
import {
  ResourceMasterDetail,
  type ResourceListItemDescriptor,
  type ResourceDetailContext,
  useLocalization,
} from "@lemmonhq/uikit";
import { PrinterDevice, Category } from "@lemmonhq/types";
import { setTitle } from "@lemmonhq/orders";
import epsonImageURL from "@app/images/printers/epson-tm30iii3.png?url";
import { printersAPI, type CreatePrinterData } from "@app/data/api/printers";
const formatCategories = (categories: PrinterDevice["device"]["categories"]) =>
  categories
    .map((category) => category.name)
    .slice(0, 3)
    .join(", ");

export const PrintersSettings = defineComponent({
  name: "PrintersSettings",
  setup() {
    const { t } = useLocalization();
    const printers = ref<PrinterDevice[]>([]);
    const loading = ref(true);
    const categories = ref<Category[]>([]);
    const formData = ref<CreatePrinterData>({
      name: "",
      ip: "",
      port: "8043",
      serial_number: "",
      device_id: "local_printer",
      print_receipt: 0,
      p_category_ids: [],
    });
    const editingPrinterId = ref<number | null>(null);
    const submitting = ref(false);

    const loadData = async () => {
      try {
        loading.value = true;
        const [printersData, servicesData] = await Promise.all([
          devicesAPI.getPrintersV2(),
          api.getServices(),
        ]);
        printers.value = printersData;

        // Extract categories from services
        const allCategories: Category[] = [];
        servicesData.forEach((service) => {
          if (service.categories) {
            allCategories.push(...service.categories);
          }
        });

        // Remove duplicates based on id
        const uniqueCategories = allCategories.filter(
          (category, index, self) =>
            index === self.findIndex((c) => c.id === category.id),
        );

        categories.value = uniqueCategories;
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      setTitle(t("general.page_titles.printers"));
      await loadData();
    });

    const describePrinter = (
      printer: PrinterDevice,
    ): ResourceListItemDescriptor => {
      const categorySummary = formatCategories(printer.device.categories);
      return {
        id: printer.device.guid ?? printer.device.id,
        title: printer.device.name,
        subtitle: printer.device.serial_number
          ? `Serial ${printer.device.serial_number}`
          : undefined,
        status: {
          label: "Online",
          tone: "success",
          variant: "soft",
        },
        tags: categorySummary ? [] : undefined,
        leading: () => (
          <img
            src={epsonImageURL}
            alt="Epson thermal printer"
            class="h-16 w-auto"
          />
        ),
        meta: [
          {
            icon: "lan",
            label: printer.device.ip
              ? `IP ${printer.device.ip}`
              : "IP not assigned",
          },
          {
            icon: "receipt_long",
            label:
              printer.device.print_receipt === 1
                ? "Receipts enabled"
                : "Receipts disabled",
          },
        ],
      };
    };

    const renderDetail = ({
      mode,
      item,
      close,
    }: ResourceDetailContext<unknown>) => {
      const printer = (item as PrinterDevice) ?? null;

      const handleSubmit = async (event: Event) => {
        event.preventDefault();
        if (submitting.value) return;

        try {
          submitting.value = true;

          if (mode === "create") {
            await printersAPI.createPrinter({
              ...formData.value,
              name: formData.value.name || "",
            });
          } else if (editingPrinterId.value) {
            await printersAPI.updatePrinter(
              editingPrinterId.value,
              formData.value,
            );
          }

          await loadData();
          close();
        } catch (error) {
          console.error("Failed to save printer:", error);
        } finally {
          submitting.value = false;
        }
      };

      const handleDelete = async () => {
        if (!editingPrinterId.value || submitting.value) return;

        try {
          submitting.value = true;
          await printersAPI.deletePrinter(editingPrinterId.value);
          await loadData();
          close();
        } catch (error) {
          console.error("Failed to delete printer:", error);
        } finally {
          submitting.value = false;
        }
      };

      // Initialize form data based on mode
      if (mode === "create") {
        formData.value = {
          name: "",
          ip: "",
          port: "8043",
          serial_number: "",
          device_id: "local_printer",
          print_receipt: 0,
          p_category_ids: [],
        };
        editingPrinterId.value = null;
      } else if (printer) {
        formData.value = {
          name: printer.device.name,
          ip: printer.device.ip || "",
          port: printer.device.port || "8043",
          serial_number: printer.device.serial_number || "",
          device_id: printer.device.device_id || "local_printer",
          print_receipt: printer.device.print_receipt,
          p_category_ids: printer.device.categories?.map((c) => c.id) || [],
        };
        editingPrinterId.value = printer.device.id;
      }

      return (
        <form class="flex h-full flex-col" onSubmit={handleSubmit}>
          <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
            <div class="space-y-1">
              <p class="text-xs uppercase tracking-wide text-base-content/60">
                Printer
              </p>
              <h3 class="text-xl font-semibold text-base-content">
                {mode === "create" ? "Add a new printer" : printer?.device.name}
              </h3>
              <p class="text-sm text-base-content/70">
                Configure thermal printers and route orders to the right
                station.
              </p>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <label class="form-control w-full md:col-span-2">
                <span class="label-text text-sm font-medium text-base-content/80">
                  Printer name
                </span>
                <input
                  type="text"
                  value={formData.value.name}
                  onInput={(e) => {
                    formData.value.name = (e.target as HTMLInputElement).value;
                  }}
                  placeholder="Kitchen printer"
                  class="input input-bordered w-full"
                  required
                />
              </label>

              <label class="form-control w-full">
                <span class="label-text text-sm font-medium text-base-content/80">
                  IP address
                </span>
                <input
                  type="text"
                  value={formData.value.ip}
                  onInput={(e) => {
                    formData.value.ip = (e.target as HTMLInputElement).value;
                  }}
                  placeholder="192.168.0.12"
                  class="input input-bordered w-full"
                />
              </label>

              <label class="form-control w-full">
                <span class="label-text text-sm font-medium text-base-content/80">
                  Serial number
                </span>
                <input
                  type="text"
                  value={formData.value.serial_number}
                  onInput={(e) => {
                    formData.value.serial_number = (
                      e.target as HTMLInputElement
                    ).value;
                  }}
                  placeholder="Serial number"
                  class="input input-bordered w-full"
                />
              </label>

              <label class="form-control w-full">
                <span class="label-text text-sm font-medium text-base-content/80">
                  Receipt printing
                </span>
                <select
                  class="select select-bordered w-full"
                  value={
                    formData.value.print_receipt === 1 ? "enabled" : "disabled"
                  }
                  onChange={(e) => {
                    formData.value.print_receipt =
                      (e.target as HTMLSelectElement).value === "enabled"
                        ? 1
                        : 0;
                  }}
                >
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </label>

              <label class="form-control w-full md:col-span-2">
                <span class="label-text text-sm font-medium text-base-content/80">
                  Assigned categories
                </span>
                <div class="space-y-2">
                  {categories.value.map((category) => (
                    <label key={category.id} class="cursor-pointer label">
                      <div class="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          class="checkbox checkbox-primary checkbox-sm"
                          checked={
                            formData.value.p_category_ids?.includes(
                              category.id,
                            ) || false
                          }
                          onChange={(e) => {
                            const isChecked = (e.target as HTMLInputElement)
                              .checked;
                            const currentIds =
                              formData.value.p_category_ids || [];

                            if (isChecked) {
                              formData.value.p_category_ids = [
                                ...currentIds,
                                category.id,
                              ];
                            } else {
                              formData.value.p_category_ids = currentIds.filter(
                                (id) => id !== category.id,
                              );
                            }
                          }}
                        />
                        <span class="label-text text-sm">{category.name}</span>
                      </div>
                    </label>
                  ))}
                  {categories.value.length === 0 && (
                    <p class="text-sm text-base-content/50">
                      No categories available
                    </p>
                  )}
                </div>
              </label>
              <label class="form-control w-full md:col-span-2">
                <span class="label-text text-sm font-medium text-base-content/80">
                  {t("devices.printers.delete_printer")}
                </span>
                <div class="py-2">
                  {mode === "edit" && (
                    <button
                      type="button"
                      class="btn btn-error"
                      onClick={handleDelete}
                      disabled={submitting.value}
                    >
                      {submitting.value ? "Deleting..." : "Delete"}
                    </button>
                  )}
                </div>
              </label>
            </div>
          </div>

          <div class="flex items-center justify-between gap-4 border-t  bg-base-200/40 px-6 py-4">
            <div class="text-xs text-base-content/60">
              Keep printers on the same network for reliable tickets.
            </div>
            <div class="flex gap-2">
              <button
                type="button"
                class="btn btn-ghost btn-sm"
                onClick={() => close()}
                disabled={submitting.value}
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary btn-sm"
                disabled={submitting.value || !formData.value.name.trim()}
              >
                {submitting.value
                  ? mode === "create"
                    ? "Adding..."
                    : "Saving..."
                  : mode === "create"
                    ? "Add printer"
                    : "Save changes"}
              </button>
            </div>
          </div>
        </form>
      );
    };

    return () => (
      <div class="flex-1 px-6 py-6">
        {loading.value ? (
          <div class="flex h-80 items-center justify-center">
            <span class="loading loading-spinner loading-lg text-primary"></span>
          </div>
        ) : (
          <ResourceMasterDetail
            title="Printers"
            description="Add or update receipt and kitchen printers, keeping every ticket routed correctly."
            resourceName="Printer"
            createLabel="Add printer"
            sheetWidth="520px"
            items={printers.value}
            renderListItem={(printer) => {
              const typed = printer as PrinterDevice;
              return describePrinter(typed);
            }}
            renderDetail={(context) => renderDetail(context)}
            getKey={(printer) => {
              const typed = printer as PrinterDevice;
              return typed.device.guid || `printer-${typed.device.id}`;
            }}
          />
        )}
      </div>
    );
  },
});
